// src/pages/AdminPage.jsx
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { auth, storage, db } from "../firebase";
import {
  ref,
  listAll,
  getDownloadURL,
  uploadBytesResumable,
  deleteObject,
  updateMetadata,
} from "firebase/storage";
import { collection, getDocs } from "firebase/firestore";
import { folderKeysFor, fetchDownloadsForFolder } from "../lib/downloads";
import { fetchLikesForUser } from "../lib/likes";
import {
  RETENTION_DAYS,
  clearRetention,
  daysLeft,
  fetchRetention,
  startRetention,
  toDate,
} from "../lib/retention";
import {
  fetchAccess,
  setAlbumUnlocked,
  setUnlocked as setGalleryUnlocked,
} from "../lib/galleryAccess";
import { albumName, albumOf } from "../lib/albums";
import { competitionLabel, fetchCompetitions } from "../lib/competitions";
import { useTranslation } from "react-i18next";
import { DISCIPLINES, disciplineKey } from "../constants";
import "../style.css";

const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || "lensdance29@gmail.com";

/* Riding discipline (`users/{uid}.discipline`, set from /change-discipline).
   Profiles created before the field existed simply have none. */
const NO_DISCIPLINE = "__none__";

/* ---------------- helpers: filenames & metadata ---------------- */

function guessExtFromMime(mime = "") {
  const m = mime.toLowerCase();
  if (m.includes("jpeg")) return "jpg";
  if (m.includes("jpg")) return "jpg";
  if (m.includes("png")) return "png";
  if (m.includes("gif")) return "gif";
  if (m.includes("webp")) return "webp";
  if (m.includes("mp4")) return "mp4";
  if (m.includes("quicktime")) return "mov";
  if (m.includes("mov")) return "mov";
  if (m.includes("mkv")) return "mkv";
  if (m.includes("avi")) return "avi";
  return "bin";
}

function sanitizeFilename(name, fallbackMime) {
  const hasExt = /\.[A-Za-z0-9]{2,5}$/.test(name);
  const ext = hasExt ? name.split(".").pop() : guessExtFromMime(fallbackMime);
  const base = (hasExt ? name.slice(0, -(ext.length + 1)) : name)
    .replace(/[/\\:*?"<>|]/g, "_")
    .replace(/\s+/g, "_")
    .replace(/[\u0000-\u001f]/g, ""); // eslint-disable-line no-control-regex
  return `${base || "file"}.${ext}`;
}

function downloadableMetadata(filename, mime) {
  const safeName = String(filename).replace(/"/g, "");
  return {
    contentType: mime || "application/octet-stream",
    cacheControl: "public, max-age=3600",
    contentDisposition: `attachment; filename="${safeName}"`,
  };
}

/* ---------------------------------------------------------------- */

export default function AdminPage() {
  const [allFolders, setAllFolders] = useState([]); // שומר את כל התיקיות מהשרת
  const [userFolders, setUserFolders] = useState([]); // התיקיות שמוצגות בפועל (אחרי סינון)
  const [currentFolder, setCurrentFolder] = useState(null);
  const [mediaItems, setMediaItems] = useState([]);
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { t, i18n } = useTranslation();
  const disciplineLabel = (id) => t(disciplineKey(id));
  const [searchTerm, setSearchTerm] = useState("");
  const [disciplineFilter, setDisciplineFilter] = useState(""); // "" = all
  const [sortBy, setSortBy] = useState("name"); // "name" | "discipline"
  const [users, setUsers] = useState([]); // Firestore user profiles (name, email, uid…)
  const [downloads, setDownloads] = useState([]);
  const [downloadsLoading, setDownloadsLoading] = useState(false);
  /* The photos this client hearted in their own gallery. The visual version —
     hearts drawn on the pictures themselves — is on /me?uid=…, linked from the
     button above; this list is the quick read: what they loved, and when. */
  const [likes, setLikes] = useState([]);
  const [likesLoading, setLikesLoading] = useState(false);

  /* The 30-day clock for the folder on screen. null = not started. */
  const [retention, setRetention] = useState(null);
  const [retentionBusy, setRetentionBusy] = useState(false);

  const onStartRetention = async () => {
    const u = userForFolder(currentFolder);
    if (!u?.uid) { setError(t("admin.retentionNoAccount")); return; }
    if (!window.confirm(t("admin.retentionConfirm", { days: RETENTION_DAYS }))) return;
    setRetentionBusy(true);
    try {
      await startRetention({ uid: u.uid, folder: currentFolder, email: u.email });
      setRetention(await fetchRetention(u.uid));
    } catch (e) {
      setError(t("common.errorWithCode", { detail: e?.code || e?.message || "" }));
    } finally {
      setRetentionBusy(false);
    }
  };

  const onStopRetention = async () => {
    const u = userForFolder(currentFolder);
    if (!u?.uid) return;
    if (!window.confirm(t("admin.retentionStopConfirm"))) return;
    setRetentionBusy(true);
    try {
      await clearRetention(u.uid);
      setRetention(null);
    } catch (e) {
      setError(t("common.errorWithCode", { detail: e?.code || e?.message || "" }));
    } finally {
      setRetentionBusy(false);
    }
  };
  /* Has this client's gallery been released for download?
     null while unknown, so the button never claims a state it has not read.
     See src/lib/galleryAccess.js — locked is the default, by absence. */
  const [unlocked, setUnlocked] = useState(null);
  /* The full picture: whether everything is released, and which individual
     competitions are. The boolean above is kept because the banner and the
     all-or-nothing button read it directly. */
  const [access, setAccess] = useState(null);
  const [accessBusy, setAccessBusy] = useState(false);

  /* Which competition the next upload is filed under. "" puts the files
     straight into the client's folder, which is how everything before this
     was stored. Remembered between uploads on purpose: a competition weekend
     is uploaded client after client, and re-picking it twenty times is twenty
     chances to pick the wrong one. */
  const [uploadAlbum, setUploadAlbum] = useState("");
  const [albumOptions, setAlbumOptions] = useState([]);


  const onToggleAccess = async () => {
    const u = userForFolder(currentFolder);
    if (!u?.uid) { setError(t("admin.retentionNoAccount")); return; }
    const next = !unlocked;
    // Only releasing is confirmed. Re-locking is the reversible direction and
    // the cautious one; asking twice for it trains the habit of clicking
    // through the dialog that matters.
    if (next && !window.confirm(t("admin.unlockConfirm"))) return;
    setAccessBusy(true);
    try {
      await setGalleryUnlocked(u.uid, next);
      setUnlocked(next);
      setAccess((a) => ({ ...(a || { albums: [] }), all: next }));
    } catch (e) {
      setError(t("common.errorWithCode", { detail: e?.code || e?.message || "" }));
    } finally {
      setAccessBusy(false);
    }
  };

  const inputRef = useRef(null);

  const user = auth.currentUser;
  const isAdmin = !!user && user.email === ADMIN_EMAIL;

  // Match a Storage folder name to its Firestore user profile
  /* Match a Storage folder name back to its account.
     `folderKey` is checked first and explicitly: it is the canonical name
     stored on the profile, and it is what a client with no folder yet is
     listed under — but it is not necessarily one of the spellings
     folderKeysFor() derives from the email, so matching on those alone left
     exactly those clients with no account attached. Everything keyed on the
     uid then went quiet: no likes, no retention, and a download lock stuck
     on "loading" behind a button that could never be pressed. */
  const userForFolder = (folder) =>
    users.find((u) =>
      (u.folderKey && u.folderKey === folder)
      || folderKeysFor({ email: u.email, uid: u.uid }).includes(folder));

  // --- Fetch User Folders from Storage (With Debug Logs) ---
  const fetchUserFolders = async () => {
    try {
      setError("");
      setBusy(true);
      console.log("Starting to fetch folders from root...");

      const listRef = ref(storage, "/");
      const res = await listAll(listRef);

      console.log("Raw Response from Firebase:", res);
      console.log("Prefixes (Folders) found:", res.prefixes.length);
      console.log("Items (Files) found at root:", res.items.length);

      // חילוץ שמות התיקיות (Prefixes)
      const folders = res.prefixes.map((folderRef) => folderRef.name);
      
      // חילוץ קבצים שאולי נמצאים בשורש בטעות במקום בתוך תיקייה
      const filesAtRootAsFolders = res.items.map(item => item.name);
      
      // איחוד כדי לראות את כל מה שקיים בשורש (ללא כפילויות)
      const allDetectedUsers = Array.from(new Set([...folders, ...filesAtRootAsFolders]));

      console.log("Final processed folder list:", allDetectedUsers);

      setAllFolders(allDetectedUsers);
      setUserFolders(allDetectedUsers);

      if (allDetectedUsers.length === 0) {
        console.warn("No folders or files found at root.");
      }

    } catch (e) {
      console.error("Error fetching folders:", e);
      setError("Failed to load user folders. Check console for details.");
    } finally {
      setBusy(false);
    }
  };

  // --- Fetch Media from a Specific Folder (מדלגים על .placeholder) ---
  /** listAll, but through sub-folders too — a competition album is one. */
  const listAllDeep = async (folderRef) => {
    const res = await listAll(folderRef);
    const items = [...res.items];
    for (const prefix of res.prefixes) {
      // eslint-disable-next-line no-await-in-loop
      items.push(...(await listAllDeep(prefix)));
    }
    return items;
  };

  const fetchMediaInFolder = async (folderName) => {
    try {
      setBusy(true);
      setError("");
      const folderRef = ref(storage, folderName);
      /* Deep, because photos now live one level down in a competition folder.
         Left flat, this page would show an empty gallery the moment Alina
         filed an upload under a competition — the files are there, just not
         at the level the old listing looked at. */
      const res = { items: await listAllDeep(folderRef) };

      /* Thumbnails, not originals.
         This grid used to point every <img> at the full-resolution download
         URL. A folder of thirty 8 MB photographs then decoded a couple of
         hundred megabytes of bitmap at once — Chrome survives it, Safari kills
         the tab, and a killed tab looks exactly like a blank white page with
         the whole app gone. /me has always resized through /api/image; this
         page simply never did.
         The original stays on `url` for Open and Delete, which need the real
         file. Only what is painted is shrunk. */
      const isProduction =
        window.location.hostname !== "localhost" &&
        !window.location.hostname.startsWith("127.");

      const mediaPromises = res.items.map(async (itemRef) => {
        if (itemRef.name === ".placeholder") return null;

        const url = await getDownloadURL(itemRef);
        const type = itemRef.name.split(".").pop();
        const isVid = /^(mp4|mov|avi|webm|mkv|m4v)$/i.test(type || "");
        const thumbUrl = !isVid && isProduction
          ? `/api/image?url=${encodeURIComponent(url)}&w=480&q=70`
          : url;

        return {
          id: itemRef.fullPath,
          url,
          thumbUrl,
          isVideo: isVid,
          type,
          name: itemRef.name,
          // Which competition this one was filed under, "" for the older
          // photos that sit directly in the client's folder.
          album: albumOf(itemRef.fullPath, folderName),
        };
      });

      const mediaData = (await Promise.all(mediaPromises)).filter(Boolean);
      setMediaItems(mediaData);
      setBusy(false);
    } catch (e) {
      console.error(e);
      setError("Failed to load media for this folder.");
      setBusy(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchUserFolders();
    }
  }, [isAdmin]);

  /* The competitions Alina can file an upload under — every one she has
     created, newest first, because photographs are uploaded days after the
     event and almost never for something far in the past. */
  useEffect(() => {
    if (!isAdmin) return;
    fetchCompetitions()
      .then((list) => setAlbumOptions(
        [...list]
          .sort((a, b) => String(b.startDate || "").localeCompare(String(a.startDate || "")))
          .map(competitionLabel)
          .filter(Boolean)
      ))
      .catch((e) => console.warn("Could not load competitions:", e));
  }, [isAdmin]);

  // Load Firestore user profiles so folders can show real full names
  useEffect(() => {
    if (!isAdmin) return;
    getDocs(collection(db, "users"))
      .then((snap) => setUsers(snap.docs.map((d) => ({ uid: d.id, ...d.data() }))))
      .catch((e) => console.warn("Could not load user profiles:", e));
  }, [isAdmin]);

  // Open a user folder: load its media AND that user's download history
  const openFolder = async (folder) => {
    setCurrentFolder(folder);
    fetchMediaInFolder(folder);
    setDownloads([]);
    setLikes([]);
    setDownloadsLoading(true);
    try {
      setDownloads(await fetchDownloadsForFolder(folder));
    } catch (e) {
      console.warn("Could not load downloads:", e);
    } finally {
      setDownloadsLoading(false);
    }

    /* Likes are keyed by uid, not by folder name — a folder is derived from an
       email and several spellings map to the same person, whereas the uid is
       the account itself. An account-less folder simply has no likes to show. */
    const uid = userForFolder(folder)?.uid;
    setRetention(null);
    setUnlocked(null);
    if (!uid) return;
    fetchAccess(uid).then((a) => { setAccess(a); setUnlocked(a.all); });
    setLikesLoading(true);
    try {
      setLikes(await fetchLikesForUser(uid));
    } catch (e) {
      console.warn("Could not load likes:", e);
    } finally {
      setLikesLoading(false);
    }
    try {
      setRetention(await fetchRetention(uid));
    } catch (e) {
      console.warn("Could not load retention:", e);
    }
  };

  /* ── WHO APPEARS IN THE LIST ────────────────────────────────────────────
     The list used to be exactly "the folders that exist in Storage", which
     meant a client could only be found once she already had photos. Alina
     would search a brand-new client by email, get "No user galleries found",
     and have no way to upload the first photo to her — the folder she needed
     could only be created by the upload she could not reach.

     So a registered account is enough to show up. Every user profile
     contributes its expected folder name (the stored folderKey when there is
     one, otherwise the sanitized email) unless a folder for that person is
     already listed. Firebase Storage has no real directories — a path is
     created by the first file written to it — so uploading to a name that is
     not there yet is exactly how it is supposed to work.

     Storage roots that are not people are dropped at the same time: the
     public gallery, the covers folder, and stray files sitting at the root,
     which used to appear as though "pic3.png" were a client. */
  /* NOT hidden: MainGallery. It is not a client, but it IS the public
     gallery on the front page, and this list is the only way to upload to it
     — filtering it out as "not a person" took away Alina's way of adding
     photos to the home page. It is pinned to the top and labelled instead.
     The rest really are plumbing: cover images, and stray files sitting at
     the Storage root that used to appear as though "pic3.png" were a client. */
  const SYSTEM_FOLDERS = React.useMemo(
    () => new Set(["covers", "public", "images", "thumbs"]),
    []
  );

  /** The public gallery's folder — shown first, and never treated as a client. */
  const MAIN_GALLERY = "MainGallery";
  const looksLikeFile = (name) => /\.[A-Za-z0-9]{2,5}$/.test(name);

  const knownFolders = React.useMemo(() => {
    const out = [];
    const seen = new Set();

    allFolders.forEach((folder) => {
      if (SYSTEM_FOLDERS.has(folder) || looksLikeFile(folder)) return;
      out.push(folder);
      seen.add(folder);
    });

    users.forEach((u) => {
      const candidates = folderKeysFor({ email: u.email, uid: u.uid });
      // Already on screen under one of its spellings — nothing to add.
      if (candidates.some((k) => seen.has(k))) return;
      const key = u.folderKey || candidates[0];
      if (!key || seen.has(key) || SYSTEM_FOLDERS.has(key)) return;
      out.push(key);
      seen.add(key);
    });

    return out;
  }, [allFolders, users, SYSTEM_FOLDERS]);

  /** Nothing uploaded yet — shown as a hint on the card, not hidden. */
  const isEmptyFolder = (folder) => !allFolders.includes(folder);

  /* The competitions actually present in the open folder, taken from the
     files themselves rather than from the competition list: what matters here
     is what this client HAS, not everything Alina has ever run. "" is the
     loose photos from before albums existed, and it sorts last. */
  const folderAlbums = React.useMemo(() => {
    const seen = [];
    for (const item of mediaItems) {
      const album = item.album || "";
      if (!seen.includes(album)) seen.push(album);
    }
    return seen.sort((a, b) => (a ? 0 : 1) - (b ? 0 : 1));
  }, [mediaItems]);

  // --- Live search + discipline filter + sort ---
  // Matches full name, email or folder; then narrows to one riding discipline
  // and orders the result. Runs on every keystroke over an in-memory list, so
  // there's no need to debounce it.
  useEffect(() => {
    const term = searchTerm.trim().toLowerCase();

    let list = knownFolders;

    if (term !== "") {
      list = list.filter((folder) => {
        const u = userForFolder(folder);
        const haystack = [folder, u?.name, u?.email, u?.username, disciplineLabel(u?.discipline)]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(term);
      });
    }

    if (disciplineFilter) {
      list = list.filter((folder) => {
        const d = userForFolder(folder)?.discipline;
        // The "no category yet" bucket has to be selectable too, otherwise
        // older profiles are invisible whenever a filter is on.
        return disciplineFilter === NO_DISCIPLINE ? !d : d === disciplineFilter;
      });
    }

    const byName = (folder) =>
      (userForFolder(folder)?.name || folder || "").toLocaleLowerCase("he");

    const sorted = [...list].sort((a, b) => {
      // The public gallery is not a client and does not belong in the
      // alphabet — it is the one folder Alina reaches for by name.
      if (a === MAIN_GALLERY) return -1;
      if (b === MAIN_GALLERY) return 1;
      if (sortBy === "discipline") {
        // Group by category, keeping the uncategorised users last, then sort
        // by name inside each group.
        const rank = (folder) => {
          const d = userForFolder(folder)?.discipline;
          const i = DISCIPLINES.findIndex((x) => x.id === d);
          return i === -1 ? DISCIPLINES.length : i;
        };
        const diff = rank(a) - rank(b);
        if (diff !== 0) return diff;
      }
      return byName(a).localeCompare(byName(b), "he");
    });

    setUserFolders(sorted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, disciplineFilter, sortBy, knownFolders, users, t]);

  // --- Multi-file upload ---
  const onPickFiles = (e) => {
    const selected = Array.from(e.target.files || []);
    setFiles(selected);
    setProgress({});
    setUploadErrors({});
  };

  const uploadAll = async () => {
    if (!files.length || !currentFolder) return;

    try {
      setBusy(true);
      setError("");
      setProgress({});
      setUploadErrors({});

      /* Everything in this batch goes into the competition Alina picked.
         The album is part of the path rather than metadata on each file: a
         listing already returns paths, so nothing extra has to be fetched to
         know which weekend a photo belongs to, and it stays legible in the
         Firebase console. Empty means straight into the client's folder, the
         way every upload worked before this existed. */
      const album = albumName(uploadAlbum);

      for (const f of files) {
        const safeName = sanitizeFilename(f.name, f.type);
        const fileRef = ref(storage, album
          ? `${currentFolder}/${album}/${safeName}`
          : `${currentFolder}/${safeName}`);
        const meta = downloadableMetadata(safeName, f.type);

        const task = uploadBytesResumable(fileRef, f, meta);

        await new Promise((resolve) => {
          task.on(
            "state_changed",
            (snap) => {
              const pct = Math.round(
                (snap.bytesTransferred / snap.totalBytes) * 100
              );
              setProgress((prev) => ({ ...prev, [f.name]: pct }));
            },
            (err) => {
              console.error("Upload failed for", f.name, err);
              setUploadErrors((prev) => ({ ...prev, [f.name]: err.message }));
              resolve();
            },
            () => {
              resolve();
            }
          );
        });
      }

      setFiles([]);
      if (inputRef.current) inputRef.current.value = "";
      await fetchMediaInFolder(currentFolder);

      if (Object.keys(uploadErrors).length > 0) {
        setError("Some files failed to upload. See list below.");
      }
    } catch (e) {
      console.error(e);
      setError("Upload failed.");
      alert("Upload failed: " + e.message);
    } finally {
      setBusy(false);
    }
  };

  // --- Force attachment metadata for existing files ---
  const forceAttachmentForExisting = async () => {
    if (!currentFolder || !mediaItems.length) return;
    if (!window.confirm("Update all files in this folder to be downloadable?")) return;

    try {
      setBusy(true);
      for (const it of mediaItems) {
        const fileRef = ref(storage, it.id);
        const safeName = sanitizeFilename(it.name || it.id.split("/").pop(), "");
        const meta = downloadableMetadata(
          safeName,
          `image/${(it.type || "jpeg").toLowerCase()}`
        );
        await updateMetadata(fileRef, meta).catch((err) => {
          console.warn("updateMetadata failed for", it.id, err);
        });
      }
      await fetchMediaInFolder(currentFolder);
      alert("Updated metadata. New uploads will already be downloadable.");
    } catch (e) {
      console.error(e);
      alert("Failed to update existing files: " + e.message);
    } finally {
      setBusy(false);
    }
  };

  // --- Delete single item (placeholder לא מגיע לפה בכלל) ---
  const del = async (fullPath) => {
    if (!window.confirm("Delete this item?")) return;

    try {
      setBusy(true);
      setError("");

      const fileRef = ref(storage, fullPath);
      await deleteObject(fileRef);

      await fetchMediaInFolder(currentFolder);
    } catch (e) {
      console.error(e);
      setError("Delete failed.");
      alert("Delete failed: " + e.message);
    } finally {
      setBusy(false);
    }
  };

  // --- Delete all items in current folder (לא מוחק placeholder) ---
  const deleteAllInCurrentFolder = async () => {
    if (!currentFolder || !mediaItems.length) return;

    const ok = window.confirm(
      t("admin.confirmDeleteAll")
    );
    if (!ok) return;

    try {
      setBusy(true);
      setError("");

      const itemsToDelete = mediaItems.filter((it) => it.name !== ".placeholder");

      const failed = [];
      for (const item of itemsToDelete) {
        try {
          await deleteObject(ref(storage, item.id));
        } catch (e) {
          // "object-not-found" is not a failure: the goal is for the file to be
          // gone, and it already is. This shows up when the listing the page is
          // working from is older than the bucket.
          if (e?.code === "storage/object-not-found") continue;
          failed.push(item.name);
          console.error("Failed to delete", item.id, e);
        }
      }

      // Say so rather than silently leaving files behind — the grid refreshes
      // either way, so without this the leftovers just look like a glitch.
      if (failed.length) {
        setError(`${failed.length} file(s) could not be deleted: ${failed.join(", ")}`);
      }

      await fetchMediaInFolder(currentFolder);
    } catch (e) {
      console.error(e);
      setError("Delete all failed.");
      alert("Delete all failed: " + e.message);
    } finally {
      setBusy(false);
    }
  };

  // --- UI ---
  if (!user) {
    return (
      <main className="container" style={{ textAlign: "center" }}>
        <h2 className="section-title">Admin Panel</h2>
        <p>You’re not logged in.</p>
        <Link className="auth-primary" to="/login">
          Log in
        </Link>
      </main>
    );
  }

  if (!isAdmin) {
    return (
      <main className="container" style={{ textAlign: "center" }}>
        <h2 className="section-title">Admin Panel</h2>
        <p>Not authorized</p>
      </main>
    );
  }

  return (
    /* Kept out of Google Translate's reach, like /admin/registrations: it
       rewrites text nodes on every DOM change, this page changes on every
       upload tick, and the two take turns until the page visibly judders.
       It is also the page Alina was on when Safari died of exactly that
       (src/lib/translateDomGuard.js). Nothing here is ever read by a client. */
    <main className="container" translate="no">
      <h2 className="section-title">Admin Panel</h2>
      
      {/* Search + riding-discipline filter + sort */}
      <div style={{ marginBottom: "20px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
        <input
          type="text"
          placeholder={t("registrations.search")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            padding: "10px",
            borderRadius: "5px",
            border: "1px solid #ccc",
            flex: "1 1 220px",
            minWidth: 180,
          }}
        />

        <select
          value={disciplineFilter}
          onChange={(e) => setDisciplineFilter(e.target.value)}
          title={t("admin.filterByDiscipline")}
          style={{ padding: "10px", borderRadius: "5px", border: "1px solid #ccc" }}
        >
          <option value="">{t("registrations.allCategories")}</option>
          {DISCIPLINES.map((d) => (
            <option key={d.id} value={d.id}>{t(disciplineKey(d.id))}</option>
          ))}
          <option value={NO_DISCIPLINE}>{t("disciplines.none")}</option>
        </select>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          title={t("admin.sort")}
          style={{ padding: "10px", borderRadius: "5px", border: "1px solid #ccc" }}
        >
          <option value="name">{t("admin.sortByName")}</option>
          <option value="discipline">{t("admin.sortByDiscipline")}</option>
        </select>

        {(searchTerm || disciplineFilter || sortBy !== "name") && (
          <button
            className="filter-button"
            onClick={() => { setSearchTerm(""); setDisciplineFilter(""); setSortBy("name"); }}
            style={{ margin: 0 }}
          >
            Clear
          </button>
        )}
      </div>

      {/* Folder Selection */}
      {!currentFolder ? (
        <div>
          <h3 style={{ marginBottom: "15px" }}>Select a User Gallery:</h3>
          {busy ? (
             <p>Loading users...</p>
          ) : (
            <div className="gallery-grid">
              {userFolders.map((folder) => {
                const u = userForFolder(folder);
                const isMain = folder === MAIN_GALLERY;
                return (
                  <button
                    key={folder}
                    className="filter-button"
                    onClick={() => openFolder(folder)}
                    style={{
                      display: "flex", flexDirection: "column", gap: 2, textAlign: "center",
                      // Marked out, because it is the one card here that is not
                      // a person and the one that changes the public site.
                      ...(isMain ? { borderColor: "#B2967D", background: "#FDF6E9" } : null),
                    }}
                  >
                    <span style={{ fontWeight: 700 }}>
                      {isMain ? t("admin.mainGallery") : (u?.name || folder)}
                    </span>
                    {isMain && (
                      <span style={{ fontSize: 10, opacity: 0.7 }}>{t("admin.mainGalleryHint")}</span>
                    )}
                    {u?.name && (
                      <span style={{ fontSize: 10, opacity: 0.6, direction: "ltr" }}>{folder}</span>
                    )}
                    {/* A riding category belongs to a person; the public
                        gallery has none, and printing "no category" on it
                        reads as a profile somebody forgot to fill in. */}
                    {!isMain && (
                      <span style={{ fontSize: 10, opacity: u?.discipline ? 0.85 : 0.4 }}>
                        {disciplineLabel(u?.discipline)}
                      </span>
                    )}
                    {/* An account with nothing in it yet. Worth saying so —
                        otherwise opening it looks like the photos failed to
                        load rather than like there are none. */}
                    {!isMain && isEmptyFolder(folder) && (
                      <span style={{ fontSize: 9, opacity: 0.55 }}>
                        {t("admin.noPhotosYet")}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {userFolders.length === 0 && !busy && !error && (
            <p>No user galleries found.</p>
          )}
          {error && <div className="auth-error">{error}</div>}
        </div>
      ) : (
        <section>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", gap: 12, flexWrap: "wrap" }}>
            <div>
              <h3 style={{ margin: 0 }}>{userForFolder(currentFolder)?.name || currentFolder}</h3>
              <span style={{ fontSize: 12, color: "#888", direction: "ltr", display: "block" }}>
                {userForFolder(currentFolder)?.email || currentFolder}
              </span>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {userForFolder(currentFolder)?.uid && (
                <Link className="filter-button" to={`/me?uid=${userForFolder(currentFolder).uid}`}>
                  {t("admin.openGallery")}
                </Link>
              )}
              <button className="filter-button" onClick={() => { setCurrentFolder(null); setDownloads([]); }}>
                Back to users
              </button>
            </div>
          </div>

          {/* ── Release for download ──
              A gallery is a preview until this is pressed: the client can see
              the photographs but gets them small, soft and watermarked, with
              no download buttons. Locked is the default and the default by
              absence, so a new client is never accidentally left open. */}
          <div style={{
            marginBottom: 16, border: "1px solid #E2D9CE", borderRadius: 8,
            background: unlocked ? "#F7FBF1" : "#FDF6E9",
            padding: "12px 14px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 12, flexWrap: "wrap",
          }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 3 }}>
                {t("admin.accessTitle")}
              </div>
              {/* A folder with no account behind it cannot be locked or
                  released — the flag is keyed on the uid. Saying so beats
                  leaving "loading" on screen forever, which is what this did
                  while it waited for a uid that was never coming. */}
              <div style={{ fontSize: 12.5, color: unlocked ? "#3B6D11" : "#8A6A22" }}>
                {!userForFolder(currentFolder)?.uid
                  ? t("admin.retentionNoAccount")
                  : unlocked === null
                    ? t("common.loading")
                    : unlocked
                      ? t("admin.accessOpen")
                      : t("admin.accessLocked")}
              </div>
            </div>
            {!!userForFolder(currentFolder)?.uid && (
              <button
                className="filter-button"
                onClick={onToggleAccess}
                disabled={accessBusy || unlocked === null}
                style={{ opacity: accessBusy || unlocked === null ? 0.6 : 1 }}
              >
                {unlocked ? t("admin.accessLock") : t("admin.accessAll")}
              </button>
            )}
          </div>

          {/* ── One competition at a time ──
              A rider pays for one weekend at a time. Releasing the whole
              gallery because one was settled hands over work nobody has paid
              for; holding it all back because another has not punishes them
              for the one they did pay for. Each album is released on its own.
              Hidden while everything is open: with the gallery fully
              released, per-album switches would only invite someone to close
              one and wonder why nothing changed. */}
          {!unlocked && !!userForFolder(currentFolder)?.uid && folderAlbums.length > 0 && (
            <div style={{
              marginBottom: 16, border: "1px solid #E2D9CE", borderRadius: 8,
              background: "#FDFAF5", padding: "12px 14px",
            }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>
                {t("admin.accessPerAlbum")}
              </div>
              {folderAlbums.map((album) => {
                const open = (access?.albums || []).includes(album);
                return (
                  <div key={album} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    gap: 12, padding: "7px 0", borderTop: "1px solid #EDE8DF", flexWrap: "wrap",
                  }}>
                    <span style={{ fontSize: 13, color: open ? "#3B6D11" : "#8A6A22" }}>
                      {album || t("admin.albumNone")}
                      {" · "}
                      {open ? t("admin.accessOpenShort") : t("admin.accessLockedShort")}
                    </span>
                    <button
                      className="filter-button"
                      disabled={accessBusy || !album}
                      style={{ opacity: accessBusy || !album ? 0.6 : 1 }}
                      onClick={async () => {
                        const u = userForFolder(currentFolder);
                        if (!u?.uid || !album) return;
                        setAccessBusy(true);
                        try {
                          await setAlbumUnlocked(u.uid, album, !open);
                          setAccess(await fetchAccess(u.uid));
                        } catch (e) {
                          setError(t("common.errorWithCode", { detail: e?.code || e?.message || "" }));
                        } finally {
                          setAccessBusy(false);
                        }
                      }}
                    >
                      {open ? t("admin.accessLock") : t("admin.accessUnlock")}
                    </button>
                  </div>
                );
              })}
              {/* Loose photos from before albums existed cannot be released on
                  their own — there is no name to key them on. The all-or-
                  nothing switch above is what opens them. */}
              {folderAlbums.includes("") && (
                <p style={{ fontSize: 11.5, color: "#8A7868", margin: "8px 0 0", lineHeight: 1.6 }}>
                  {t("admin.accessLooseNote")}
                </p>
              )}
            </div>
          )}

          {/* ── The 30-day clock ──
              Pressed when the upload for this client is finished, not when it
              starts: Alina often uploads a competition across several
              sittings, and a clock that began with the first file would burn
              days before the gallery was even complete. */}
          {(() => {
            const left = retention ? daysLeft(retention.expiresAt) : null;
            const ends = retention ? toDate(retention.expiresAt) : null;
            const expired = left !== null && left <= 0;
            return (
              <div style={{
                marginBottom: 16, border: "1px solid #E2D9CE", borderRadius: 8,
                background: retention ? (expired ? "#FFF6F4" : "#F7FBF1") : "#FDFAF5",
                padding: "12px 14px",
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: 12, flexWrap: "wrap",
              }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 3 }}>
                    {t("admin.retentionTitle")}
                  </div>
                  <div style={{ fontSize: 12.5, color: expired ? "#8A2A1F" : "#6A5A50" }}>
                    {!retention
                      ? t("admin.retentionNotStarted", { days: RETENTION_DAYS })
                      : expired
                        ? t("admin.retentionExpired")
                        : t("admin.retentionRunning", {
                            count: left,
                            date: ends ? ends.toLocaleDateString(i18n.language) : "",
                          })}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    className="filter-button"
                    onClick={onStartRetention}
                    disabled={retentionBusy}
                    style={{ opacity: retentionBusy ? 0.6 : 1 }}
                  >
                    {retention ? t("admin.retentionRestart") : t("admin.retentionStart")}
                  </button>
                  {retention && (
                    <button
                      className="filter-button"
                      onClick={onStopRetention}
                      disabled={retentionBusy}
                      style={{ opacity: retentionBusy ? 0.6 : 1 }}
                    >
                      {t("admin.retentionStop")}
                    </button>
                  )}
                </div>
              </div>
            );
          })()}

          {/* ── Photos this client marked as favourites ──
              Shown as thumbnails, not a list of filenames. The point of this
              panel is reading someone's taste at a glance — which crops, which
              light, which moments they go for — and "DSC_8944.jpg" tells you
              none of that.
              The pictures come from `mediaItems`, already loaded for this
              folder, so nothing extra is fetched. A like whose file is no
              longer in the folder still shows, as a caption with no picture,
              rather than vanishing without explanation. */}
          <div style={{ marginBottom: 16, border: "1px solid #E2D9CE", borderRadius: 8, background: "#FDFAF5" }}>
            <div style={{ padding: "10px 14px", borderBottom: "1px solid #EDE8DF", fontWeight: 700, fontSize: 14 }}>
              {t("admin.likes")} {likesLoading ? "" : `(${likes.length})`}
            </div>
            <div style={{ maxHeight: 300, overflowY: "auto", padding: "12px 14px" }}>
              {likesLoading ? (
                <p style={{ color: "#888", fontSize: 13 }}>{t("admin.likesLoading")}</p>
              ) : likes.length === 0 ? (
                <p style={{ color: "#888", fontSize: 13 }}>{t("admin.likesEmpty")}</p>
              ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                  {likes.map((l) => {
                    const media = mediaItems.find((m) => m.id === l.path);
                    const ext = (l.name || l.path || "").split(".").pop().toLowerCase();
                    const isVid = ["mp4", "mov", "webm", "m4v"].includes(ext);
                    const when = l.likedAt?.seconds
                      ? new Date(l.likedAt.seconds * 1000).toLocaleDateString(i18n.language)
                      : "";
                    return (
                      <a
                        key={l.id}
                        href={media?.url || undefined}
                        target="_blank"
                        rel="noreferrer"
                        title={`${l.name || l.path}${when ? ` · ${when}` : ""}`}
                        style={{
                          width: 118, textDecoration: "none", color: "#6A5A50",
                          cursor: media?.url ? "zoom-in" : "default",
                        }}
                      >
                        <div style={{
                          position: "relative", width: 118, height: 118,
                          background: "#EDE8DF", borderRadius: 6, overflow: "hidden",
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          {media?.url ? (
                            isVid ? (
                              <>
                                <video src={media.url} muted playsInline preload="metadata"
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                {/* preload="metadata" does not paint a frame, so a
                                    clip would otherwise be an unexplained grey
                                    square. The marker says "this is a video". */}
                                <span style={{
                                  position: "absolute", inset: 0, display: "flex",
                                  alignItems: "center", justifyContent: "center",
                                  color: "rgba(255,255,255,.9)", fontSize: 22,
                                  textShadow: "0 1px 4px rgba(0,0,0,.5)",
                                  background: "rgba(0,0,0,.18)", pointerEvents: "none",
                                }} aria-hidden="true">▶</span>
                              </>
                            ) : (
                              <img src={media.thumbUrl || media.url} alt={l.name || ""}
                                loading="lazy" decoding="async"
                                style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            )
                          ) : (
                            <span style={{ fontSize: 20, color: "#C4B7A6" }} aria-hidden="true">🖼</span>
                          )}
                          <span style={{
                            position: "absolute", top: 5, left: 5,
                            width: 20, height: 20, borderRadius: "50%",
                            background: "rgba(255,255,255,.92)", color: "#E2607A",
                            fontSize: 11, lineHeight: "20px", textAlign: "center",
                          }} aria-hidden="true">♥</span>
                        </div>
                        <div style={{
                          fontSize: 10, marginTop: 4, direction: "ltr",
                          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                        }}>
                          {l.name || l.path}
                        </div>
                        {when && <div style={{ fontSize: 9, color: "#A89D90", direction: "ltr" }}>{when}</div>}
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ── Download history for this user ── */}
          <div style={{ marginBottom: 16, border: "1px solid #E2D9CE", borderRadius: 8, background: "#FDFAF5" }}>
            <div style={{ padding: "10px 14px", borderBottom: "1px solid #EDE8DF", fontWeight: 700, fontSize: 14 }}>
              {t("admin.downloads")} {downloadsLoading ? "" : `(${downloads.length})`}
            </div>
            <div style={{ maxHeight: 220, overflowY: "auto", padding: "6px 14px" }}>
              {downloadsLoading ? (
                <p style={{ color: "#888", fontSize: 13 }}>{t("admin.downloadsLoading")}</p>
              ) : downloads.length === 0 ? (
                <p style={{ color: "#888", fontSize: 13 }}>{t("admin.downloadsEmpty")}</p>
              ) : (
                <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                  {downloads.map((d) => (
                    <li key={d.id} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "6px 0", borderBottom: "1px solid #F0EBE2", fontSize: 13 }}>
                      <span style={{ direction: "ltr", wordBreak: "break-all" }}>
                        {d.isVideo ? "🎬 " : "🖼️ "}
                        {d.url ? (
                          <a href={d.url} target="_blank" rel="noreferrer" style={{ color: "#4A3525" }}>
                            {d.fileName || d.filePath || t("admin.file")}
                          </a>
                        ) : (
                          d.fileName || d.filePath || t("admin.file")
                        )}
                      </span>
                      <span style={{ color: "#999", whiteSpace: "nowrap" }}>
                        {d.at?.seconds ? new Date(d.at.seconds * 1000).toLocaleString(i18n.language) : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Which competition this upload belongs to.
              Above the file picker, not beside it: it has to be answered
              before the files are chosen, or it becomes the thing you notice
              after pressing Upload. */}
          <div style={{
            display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
            marginBottom: 12, padding: "10px 12px",
            border: "1px solid #E2D9CE", background: "#FDFAF5", borderRadius: 8,
          }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>{t("admin.albumLabel")}</span>
            <select
              value={uploadAlbum}
              onChange={(e) => setUploadAlbum(e.target.value)}
              style={{ padding: "7px 10px", border: "1px solid #D7C9B8", fontSize: 13, minWidth: 220 }}
            >
              <option value="">{t("admin.albumNone")}</option>
              {albumOptions.map((label) => (
                <option key={label} value={label}>{label}</option>
              ))}
            </select>
            <span style={{ fontSize: 12, color: "#8A7868" }}>{t("admin.albumHint")}</span>
          </div>

          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "12px" }}>
            <input ref={inputRef} type="file" multiple onChange={onPickFiles} />
            <button className="filter-button" onClick={uploadAll} disabled={busy || !files.length}>
              Upload
            </button>
            <button className="filter-button" onClick={forceAttachmentForExisting} disabled={busy || !mediaItems.length}>
              Make all downloadable
            </button>
            <button className="filter-button" onClick={deleteAllInCurrentFolder} disabled={busy || !mediaItems.length}>
              Delete all
            </button>
          </div>

          {!!files.length && (
            <ul style={{ marginBottom: "12px" }}>
              {files.map((f) => (
                <li key={f.name}>
                  {f.name} {progress[f.name] != null ? `- ${progress[f.name]}%` : ""}
                  {uploadErrors[f.name] ? ` (Error: ${uploadErrors[f.name]})` : ""}
                </li>
              ))}
            </ul>
          )}

          {busy && <p>Loading media...</p>}

          <div className="gallery-grid">
            {mediaItems.map((item) => (
              <div key={item.id} className="gallery-item">
                {/[.]?(jpg|jpeg|png|gif|webp)$/i.test(item.name) ? (
                  /* thumbUrl, lazy, async: only what is actually on screen gets
                     downloaded and decoded. With originals and eager loading a
                     large folder could exhaust the tab's memory — see the note
                     in fetchMediaInFolder. */
                  <img
                    src={item.thumbUrl || item.url}
                    alt={item.name}
                    loading="lazy"
                    decoding="async"
                    style={{ width: "100%", borderRadius: "8px" }}
                  />
                ) : (
                  /* preload="metadata" fetches a few kilobytes of header rather
                     than buffering every clip in the folder at once. */
                  <video
                    src={item.url}
                    controls
                    preload="metadata"
                    playsInline
                    style={{ width: "100%", borderRadius: "8px" }}
                  />
                )}
                <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                  <a className="filter-button" href={item.url} target="_blank" rel="noreferrer">
                    Open
                  </a>
                  <button className="filter-button" onClick={() => del(item.id)}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {!mediaItems.length && !busy && <p>No media in this folder.</p>}
        </section>
      )}
    </main>
  );
}